import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import {
  addService,
  deleteService,
  fetchServicesByCategory,
  updateService,
} from "../../redux/adminReducer";
import Loader from "../../common/loader";
import Modal from "../../common/modal";
import { toast } from "react-toastify";

function ServiceDetail() {
  const { categoryId } = useParams();
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [newServicePic, setNewServicePic] = useState(null);
  const { translations, dir } = useSelector((state) => state.language);
  const [newServiceName, setNewServiceName] = useState("");
  const [newServiceDesc, setNewServiceDesc] = useState("");
  const [serviceToUpdate, setServiceToUpdate] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const dispatch = useDispatch();
  const serviceDescRef = useRef(null);
  const addServiceDescRef = useRef(null);

  const serviceDetails = useSelector(
    (state) => state.admin.categoryServices?.data || []
  );

  const handleAddService = () => {
    if (newServiceName) {
      setLoading(true);

      const formData = new FormData();
      formData.append("service_name", newServiceName);
      formData.append("description", newServiceDesc);
      formData.append("category_id", categoryId);

      // ✅ Only append if image is selected
      if (newServicePic) {
        formData.append("service_pic", newServicePic);
      }

      dispatch(addService(formData))
        .then(() => {
          dispatch(fetchServicesByCategory(categoryId));
          setLoading(false);
          toast.success(translations.toaster.service_added_successfully);
        })
        .catch((error) => {
          console.error("Not added", error);
          setLoading(false);
        })
        .finally(() => {
          setIsAddModalOpen(false);
          setNewServiceName("");
          setNewServiceDesc("");
          setNewServicePic(null);
        });
    }
  };

  useEffect(() => {
    setLoading(true);
    dispatch(fetchServicesByCategory(categoryId)).then(() => {
      setLoading(false);
    });
  }, [dispatch, categoryId]);

  const handleUpdateClick = (service) => {
    setServiceToUpdate(service);
    setNewServiceName(service.service_name);
    setNewServiceDesc(service.description);
    setIsUpdateModalOpen(true);
  };

  const handleUpdateService = () => {
    if (!serviceToUpdate) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("id", serviceToUpdate._id);

    if (newServiceName.trim() !== "") {
      formData.append("service_name", newServiceName.trim());
    }

    if (newServiceDesc.trim() !== "") {
      formData.append("description", newServiceDesc.trim());
    }

    if (newServicePic) {
      formData.append("service_pic", newServicePic); // ✅ Image file
    }

    dispatch(updateService(formData))
      .then(() => {
        dispatch(fetchServicesByCategory(categoryId));
        toast.success(translations.toaster.service_updated_successfully);
      })
      .catch((err) => {
        console.error("Update failed", err);
      })
      .finally(() => {
        setLoading(false);
        setIsUpdateModalOpen(false);
        setNewServiceName("");
        setNewServiceDesc("");
        setNewServicePic(null);
        setServiceToUpdate(null);
      });
  };
  const BASE_URL = process.env.REACT_APP_BASE_URL || "https://jeema.co/api/v2/";
  const handleDeleteClick = (service) => {
    setServiceToDelete(service);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (serviceToDelete) {
      setLoading(true);
      dispatch(deleteService({ id: serviceToDelete._id }))
        .then(() => {
          dispatch(fetchServicesByCategory(categoryId));
          toast.success(translations.toaster.service_deleted_successfully);
        })
        .catch((error) => {
          console.error("Delete failed", error);
        })
        .finally(() => {
          setLoading(false);
          setIsModalOpen(false);
          setServiceToDelete(null);
        });
    }
  };

  return (
    <>
      <Layout>
        <div className="min-h-screen w-full px-4 py-4">
          <section className="pt-1 grid grid-cols-2 items-center">
            <h1 className="text-xl font-bold text-white">
              {translations.category.categoryDetails}
              <small className="text-2xl font-semibold text-gray-600"> |</small>
              <small className="font-normal text-gray-600">
                {" "}
                {translations.category.categoryDetailsManagement}
              </small>
            </h1>
            <div className="text-md text-white flex items-center justify-end space-x-1">
              <div className="rounded-full bg-[#5A189A] py-2 px-3">
                <Link to="/" className="text-white no-underline cursor-pointer">
                  {translations.category.dashboard}
                </Link>
                <span className="mx-1">/</span>
                <Link to="/services" className="text-white no-underline">
                  {translations.category.categories}
                </Link>
                <span className="mx-1">/</span>
                <span className="text-green-300">
                  {translations.category.categoryDetails}
                </span>
              </div>
            </div>
          </section>

          <div className="p-4 w-full bg-gray-800 shadow-xl rounded mt-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-white">
                {translations.category.serviceList}
              </h2>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
              >
                {translations.category.addService}
              </button>
            </div>
            {loading ? (
              <Loader />
            ) : (
              <div className="mt-4">
                <ul className="space-y-2">
                  {serviceDetails.length === 0 ? (
                    <li className="text-gray-400 text-center">
                      No services available in this category.
                    </li>
                  ) : (
                    serviceDetails.map((service) => (
                      <li
                        key={service.id}
                        className="bg-gray-700 p-4 rounded-lg flex flex-col lg:flex-row lg:items-center lg:justify-between"
                      >
                        <div className="flex items-center space-x-4 rtl:space-x-reverse ">
                          {service.service_pic && (
                            <img
                              src={`${BASE_URL}${encodeURIComponent(
                                service.service_pic
                              )}`}
                              alt="service"
                              className="w-16 h-16 object-cover rounded-md"
                            />
                          )}
                          <div>
                            <h3 className="text-xl font-bold text-white">
                              {service.service_name}
                            </h3>
                            <p className="text-gray-400">
                              {service.description}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 lg:mt-0 flex flex-row gap-2 rtl:flex-row-reverse">
                          <button
                            onClick={() => handleUpdateClick(service)}
                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                          >
                            {translations.category.update}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(service)}
                            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                          >
                            {translations.category.delete}
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* category add modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        width="w-[95%] sm:w-[90%] md:w-[80%] lg:w-[60%] xl:w-[50%] max-w-2xl"
      >
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 mx-auto">
          <h3 className="text-xl text-center text-white mb-6">
            {translations.category.addNewService}
          </h3>

          {/* Service Name */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.serviceName}
          </label>
          <input
            type="text"
            value={newServiceName}
            onChange={(e) => setNewServiceName(e.target.value)}
            className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5 mb-3"
          />

          {/* Service Description */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.serviceDescription}
          </label>
          <textarea
            ref={addServiceDescRef}
            value={newServiceDesc}
            onChange={(e) => {
              setNewServiceDesc(e.target.value);
              if (addServiceDescRef.current) {
                addServiceDescRef.current.style.height = "auto";
                addServiceDescRef.current.style.height = `${addServiceDescRef.current.scrollHeight}px`;
              }
            }}
            maxLength={300}
            className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5 mb-1 resize-none overflow-hidden"
          />

          {/* Min Length Warning */}
          {newServiceDesc.length > 0 && newServiceDesc.length < 100 && (
            <p className="text-sm text-yellow-400 mb-1">
              Description must be at least 100 characters.
            </p>
          )}

          {/* Max Length Warning */}
          {newServiceDesc.length === 300 && (
            <p className="text-sm text-red-500 mb-1">
              Maximum 300 characters reached
            </p>
          )}

          {/* Character Counter */}
          <p className="text-sm text-gray-400 text-right mb-3">
            {newServiceDesc.length}/300
          </p>

          {/* Service Picture */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.servicePicture}
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewServicePic(e.target.files?.[0] || null)}
            className="w-full text-white mb-3"
          />

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
            <button
              onClick={() => {
                setIsAddModalOpen(false);
                setNewServiceName("");
                setNewServiceDesc("");
                setNewServicePic(null);
              }}
              className="w-full sm:w-auto px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-500"
            >
              {translations.category.cancel}
            </button>
            <button
              onClick={handleAddService}
              disabled={newServiceDesc.length < 100}
              className={`w-full sm:w-auto px-4 py-2 rounded text-white transition
          ${
            newServiceDesc.length < 100
              ? "bg-blue-600 opacity-50 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
            >
              {translations.category.submit}
            </button>
          </div>
        </div>
      </Modal>

      {/* category update modal */}
      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        width="w-[95%] sm:w-[90%] md:w-[80%] lg:w-[60%] xl:w-[50%] max-w-2xl"
      >
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 mx-auto">
          <h3 className="text-xl text-center mb-6 text-white">
            {translations.category.updateService}
          </h3>

          {/* Service Name */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.serviceName}
          </label>
          <input
            type="text"
            value={newServiceName}
            onChange={(e) => setNewServiceName(e.target.value)}
            className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5 mb-3"
          />

          {/* Service Description */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.description}
          </label>

          <textarea
            ref={serviceDescRef}
            value={newServiceDesc}
            onChange={(e) => {
              setNewServiceDesc(e.target.value);
              if (serviceDescRef.current) {
                serviceDescRef.current.style.height = "auto";
                serviceDescRef.current.style.height = `${serviceDescRef.current.scrollHeight}px`;
              }
            }}
            maxLength={300}
            className="w-full rounded-md border border-dark-3 bg-gray-700 text-white px-4 py-2.5 mb-3 resize-none overflow-hidden"
          />

          {/* Min Length Warning */}
          {newServiceDesc.length > 0 && newServiceDesc.length < 100 && (
            <p className="text-sm text-yellow-400 mb-1">
              Description must be at least 100 characters.
            </p>
          )}

          {/* Max Length Warning */}
          {newServiceDesc.length === 300 && (
            <p className="text-sm text-red-500 mb-1">
              Maximum 300 characters reached
            </p>
          )}

          {/* Character Counter */}
          <p className="text-sm text-gray-400 text-right">
            {newServiceDesc.length}/300
          </p>

          {/* Service Picture */}
          <label className="block text-gray-400 text-sm mb-1">
            {translations.category.servicePicture}
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewServicePic(e.target.files?.[0] || null)}
            className="w-full text-white mb-3"
          />

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
            <button
              onClick={() => {
                setIsUpdateModalOpen(false);
                setNewServiceName("");
                setNewServiceDesc("");
                setNewServicePic(null);
              }}
              className="w-full sm:w-auto px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-500"
            >
              {translations.category.cancel}
            </button>
            <button
              onClick={handleUpdateService}
              disabled={newServiceDesc.length < 100}
              className={`w-full sm:w-auto px-4 py-2 rounded text-white transition
    ${
      newServiceDesc.length < 100
        ? "bg-blue-600 opacity-50 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700"
    }`}
            >
              {translations.category.update}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        width="w-[95%] sm:w-[90%] md:w-[80%] lg:w-[60%] xl:w-[40%] max-w-md"
      >
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 text-white mx-auto">
          <h2 className="text-xl font-semibold mb-4 text-red-600 text-center sm:text-left">
            {translations.service.confirmDelete}
          </h2>

          <p className="mb-6 text-center sm:text-left">
            {translations.service.warning}{" "}
            <strong>{serviceToDelete?.service_name}</strong>{" "}
            {translations.service.service}
          </p>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              className="w-full sm:w-auto bg-gray-500 px-4 py-2 rounded hover:bg-gray-400"
              onClick={() => setIsModalOpen(false)}
            >
              {translations.service.cancel}
            </button>
            <button
              className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              onClick={() => {
                confirmDelete();
                setIsModalOpen(false);
              }}
            >
              {translations.service.delete}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ServiceDetail;
